import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  Stack,
  useTheme,
  alpha,
  IconButton,
  Skeleton
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../core/hooks/useStore";
import { RootState } from "../../../core/store/store";
import toolsSlice, {
  HeadlineData,
  HeadlineJobData,
  ToolJobData,
} from "./headline-pig.slice";
import useTool from "./use-headline-pig";

// Helper function to format time ago
const getTimeAgo = (date: Date): string => {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  
  let interval = Math.floor(seconds / 31536000);
  if (interval >= 1) {
    return interval === 1 ? "1 year ago" : `${interval} years ago`;
  }
  
  interval = Math.floor(seconds / 2592000);
  if (interval >= 1) {
    return interval === 1 ? "1 month ago" : `${interval} months ago`;
  }
  
  interval = Math.floor(seconds / 86400);
  if (interval >= 1) {
    return interval === 1 ? "1 day ago" : `${interval} days ago`;
  }
  
  interval = Math.floor(seconds / 3600);
  if (interval >= 1) {
    return interval === 1 ? "1 hour ago" : `${interval} hours ago`;
  }
  
  interval = Math.floor(seconds / 60);
  if (interval >= 1) {
    return interval === 1 ? "1 minute ago" : `${interval} minutes ago`;
  }
  
  return "Just now";
};

const HeadlinePigRecentSearches = (props: { selected?: string }) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<HeadlineJobData[]>([]);
  const [loading, setLoading] = useState(true);
  const toolsState = useSelector((state: RootState) => state.headlinePig);
  const tool = useTool();

  useEffect(() => {
    const getJobs = async () => {
      setLoading(true);
      const jobs = await tool.listJobs("HEADLINE");
      if (jobs) {
        setJobs(jobs as HeadlineJobData[]);
      } else {
        setJobs([]);
      }
      setLoading(false);
    };
    getJobs();
  }, [toolsState.tool.status]);

  const handleSearchOpen = (job: ToolJobData) => {
    // Navigate to the URL with the job ID
    navigate(`/tools/headline-tool/${job._id}`);
  };

  // Sort jobs by date and limit to recent ones
  const recentJobs = [...jobs]
    .filter((job) => (job.data as HeadlineData).niche)
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
    .slice(0, 6); // Show only 6 most recent

  // Show as inline chips for the nav bar
  if (toolsState.tool.status === "created" || toolsState.tool.status === "initialized") {
    return (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', maxWidth: 600 }}>
        {loading ? (
          <>
            <Skeleton variant="rounded" width={100} height={32} />
            <Skeleton variant="rounded" width={120} height={32} />
            <Skeleton variant="rounded" width={90} height={32} />
          </>
        ) : (
          recentJobs.slice(0, 4).map((job) => (
            <Chip
              key={job._id}
              label={job.data.niche}
              onClick={() => handleSearchOpen(job)}
              variant={props.selected === job._id ? "filled" : "outlined"}
              color={props.selected === job._id ? "primary" : "default"}
              sx={{
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontWeight: props.selected === job._id ? 600 : 400,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.05)
                }
              }}
            />
          ))
        )}
      </Box>
    );
  }

  return null;
};

// New component for showing recent searches as cards on the main page
export const HeadlinePigRecentSearchesCards = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<HeadlineJobData[]>([]);
  const [loading, setLoading] = useState(true);
  const toolsState = useSelector((state: RootState) => state.headlinePig);
  const tool = useTool();

  useEffect(() => {
    const getJobs = async () => {
      setLoading(true);
      const jobs = await tool.listJobs("HEADLINE");
      if (jobs) {
        setJobs(jobs as HeadlineJobData[]);
      } else {
        setJobs([]);
      }
      setLoading(false);
    };
    getJobs();
  }, [toolsState.tool.status]);

  const handleSearchOpen = (job: ToolJobData) => {
    // Navigate to the URL with the job ID
    navigate(`/tools/headline-tool/${job._id}`);
  };

  // Sort jobs by date
  const recentJobs = [...jobs]
    .filter((job) => (job.data as HeadlineData).niche)
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

  if (loading) {
    return (
      <Box sx={{ mt: 6, mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <AccessTimeIcon sx={{ color: theme.palette.text.secondary }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Recent Searches
          </Typography>
        </Stack>
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={140} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (recentJobs.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 6, mb: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <AccessTimeIcon sx={{ color: theme.palette.text.secondary }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Recent Searches
        </Typography>
      </Stack>
      
      <Grid container spacing={3}>
        {recentJobs.slice(0, 6).map((job) => (
          <Grid item xs={12} sm={6} md={4} key={job._id}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'all 0.3s',
                height: '100%',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[4],
                  borderColor: theme.palette.primary.main,
                  '& .open-icon': {
                    opacity: 1
                  }
                }
              }}
              onClick={() => handleSearchOpen(job)}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {job.data.niche}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                      <AccessTimeIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                      <Typography variant="caption" color="text.secondary">
                        {getTimeAgo(new Date(job.createdAt))}
                      </Typography>
                    </Stack>
                    <Chip
                      label={`${job.data.headlines?.length || 0} headlines`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                  <IconButton
                    className="open-icon"
                    size="small"
                    sx={{
                      opacity: 0,
                      transition: 'opacity 0.2s',
                      color: theme.palette.primary.main,
                      mt: -0.5
                    }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default HeadlinePigRecentSearches;